import React, { useCallback, useMemo, useState } from 'react';

import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { DataGrid, GridRowSelectionModel } from '@mui/x-data-grid';

import {
  DEPARTURE_INITIAL_SEARCH,
  DepartureCommittedSearch,
  DepartureSearchState,
  departureColumns,
} from '~/components/Cruises/components/shared/DepartureGridConfig';
import DepartureSearchForm from '~/components/Cruises/components/shared/DepartureSearchForm';
import { Port } from '~/components/Cruises/pages/Ports/types';

import { DEPARTURE_PAGE_SIZE, useGetDepartures } from '~/hooks/apis/cruises/useGetDepartures';

const PAGE_SIZE = DEPARTURE_PAGE_SIZE;

export interface SelectedDeparture {
  id: string;
  name: string;
  externalId: string;
}

interface Props {
  vendorId: string | undefined;
  vendorName: string | undefined;
  value: Array<SelectedDeparture>;
  onChange: (departures: Array<SelectedDeparture>) => void;
}

export default function DealDepartureBulkSelect({ vendorId, vendorName, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [searchFields, setSearchFields] = useState<DepartureSearchState>(DEPARTURE_INITIAL_SEARCH);
  const [selectedPorts, setSelectedPorts] = useState<Array<Port>>([]);
  const [committedSearch, setCommittedSearch] = useState<DepartureCommittedSearch | null>(null);
  const [page, setPage] = useState(0);
  const [pendingIds, setPendingIds] = useState<Array<string>>([]);
  const [labelCache, setLabelCache] = useState<Map<string, SelectedDeparture>>(new Map());

  const { data, isFetching, isError } = useGetDepartures(committedSearch, page);

  const rows = useMemo(() => data?.result ?? [], [data]);
  const total = data?.total ?? 0;

  const handleOpen = useCallback(() => {
    setPendingIds(value.map((d) => d.id));
    setLabelCache(new Map(value.map((d) => [d.id, d])));
    setOpen(true);
  }, [value]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchFields(DEPARTURE_INITIAL_SEARCH);
    setSelectedPorts([]);
    setCommittedSearch(null);
    setPage(0);
  }, []);

  const handleConfirm = useCallback(() => {
    onChange(pendingIds.map((id) => labelCache.get(id) ?? { id, name: id, externalId: '' }));
    handleClose();
  }, [pendingIds, labelCache, onChange, handleClose]);

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      const ids = (Array.isArray(model) ? model : []).map(String);
      setPendingIds(ids);
      setLabelCache((prev) => {
        const next = new Map(prev);
        rows.forEach((row) => next.set(row.id, { id: row.id, name: row.name, externalId: String(row.externalId) }));
        return next;
      });
    },
    [rows],
  );

  const handleRemove = useCallback(
    (idToRemove: string) => {
      onChange(value.filter((d) => d.id !== idToRemove));
    },
    [value, onChange],
  );

  const handleSearch = useCallback(() => {
    if (!vendorId) {
      return;
    }
    setPage(0);
    setCommittedSearch({
      ...searchFields,
      vendorId,
      departurePortExternalIds: selectedPorts.map((p) => p.externalId).filter(Boolean),
    });
  }, [searchFields, vendorId, selectedPorts]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  return (
    <Box>
      {value.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {value.map((departure) => (
            <Chip
              key={departure.id}
              label={`${departure.externalId} - ${departure.name}`}
              onDelete={() => handleRemove(departure.id)}
              size="small"
            />
          ))}
        </Stack>
      )}

      <Button variant="outlined" onClick={handleOpen} disabled={!vendorId}>
        {value.length > 0 ? 'Edit Departures' : 'Add Departures'}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
        <DialogTitle>Select Departures</DialogTitle>

        <DialogContent>
          <DepartureSearchForm
            searchFields={searchFields}
            onSearchFieldsChange={setSearchFields}
            onSearch={handleSearch}
            isSearching={isFetching}
            onKeyDown={handleKeyDown}
            vendorName={vendorName}
            selectedPorts={selectedPorts}
            onPortsChange={setSelectedPorts}
          />

          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load departures. Please try again.
            </Alert>
          )}

          {!committedSearch ? (
            <Alert severity="info">Use the filters above and click Search to find departures.</Alert>
          ) : (
            <DataGrid
              loading={isFetching}
              rows={rows}
              columns={departureColumns}
              getRowId={(row) => row.id}
              checkboxSelection
              rowSelectionModel={pendingIds}
              onRowSelectionModelChange={handleSelectionChange}
              keepNonExistentRowsSelected
              disableRowSelectionOnClick={false}
              pagination
              paginationMode="server"
              rowCount={total}
              pageSizeOptions={[PAGE_SIZE]}
              paginationModel={{ page, pageSize: PAGE_SIZE }}
              onPaginationModelChange={({ page: newPage }) => setPage(newPage)}
              autoHeight
              disableColumnMenu
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm}>
            {pendingIds.length > 0
              ? `Select ${pendingIds.length} departure${pendingIds.length > 1 ? 's' : ''}`
              : 'Select departures'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
